import { defineQuery } from "@rocicorp/zero";
import type {} from "../zero-context";
import { zql } from "../schema";

/**
 * Sidebar-specific composite queries.
 *
 * These queries use `.related()` chains to co-load everything the desktop
 * Sidebar needs in one subscription: channel memberships + each channel's
 * members (with their user rows, for DM display names) + this user's
 * per-channel stats.
 *
 * Goal: replace Sidebar's fan-out of 9+ separate subscriptions with one
 * compound IVM pipeline. Matches the idiomatic Zero pattern per the rules in
 * `.claude/rules/queries.md` ("Use `.related()` for eager loading") and the
 * zbugs `issueDetail` reference (3-level nested relationship).
 */
export const sidebarQueries = {
  /**
   * All the current user's channel memberships, each with:
   *   - `.channel` — the channel row
   *   - `.channel.parent` — the parent space row (for sidebar grouping label)
   *   - `.channel.members` — all members of that channel
   *   - `.channel.members[].user` — each member's user row (for DM name rendering)
   *   - `.stats` — the current user's `user_channel_stats` row for that channel
   *     (nullable — new channels may not have a stat row yet)
   *
   * Replaces these legacy queries for Sidebar:
   *   - channel_members.byUser (muted/hidden/notify)
   *   - user_channel_stats.byUser (unread/mention/last-message)
   *   - channels.byWorkspaceAndUserWithMembers (workspace channel list)
   *   - channels.dmsByUserWithMembers / dmsByWorkspaceAndUserWithMembers
   *     (DM rendering)
   *
   * Scope / archived / hidden filters that existed on the legacy queries run
   * client-side in Sidebar now (against the fields delivered here).
   *
   * Membership-level type filter excludes `type='space'` rows so the sidebar
   * composite never delivers space memberships to the home-scope `channelMap`
   * aggregation in Sidebar.tsx — spaces are surfaced separately. As of mig 120
   * Sidebar.tsx still consumes the alias-namespaced `client_spaces.*` queries
   * (which transparently read `channels` rows with `type='space'`); the
   * sibling `mySpacesTree` query below is the more idiomatic shape and is
   * reserved for a follow-up cleanup PR.
   */
  myChannelTree: defineQuery(({ ctx: { sub } }) =>
    zql.channel_members
      .where("user_id", sub)
      // Soft-delete guard — per `.claude/rules/queries.md` every caller-side
      // membership check MUST filter `removed_at IS NULL` (defense in depth
      // alongside the publication row filter from migration 103).
      .where("removed_at", "IS", null)
      // Filter the underlying channel rows down to the message-bearing kinds
      // so spaces don't leak into `Sidebar.tsx` home-scope `channelMap`
      // aggregation, workspace activity, unread, or warm preload behavior.
      .whereExists("channel", (cq) =>
        cq.where("type", "IN", [
          "channel",
          "dm",
          "group_dm",
        ] as readonly string[]),
      )
      // 3-level `.related()` chain matching zbugs's issueDetail shape:
      //   channel_members -> channel -> members -> user
      // `.related("user")` on each member is REQUIRED so DM display names
      // (which come from the OTHER participant) render without a second query.
      // `.related("parent")` lets sidebar render the parent-space label without
      // an extra subscription.
      .related("channel", (q) =>
        q
          .related("members", (m) =>
            // Filter kicked members so the sidebar's DM "other-user"
            // display-name lookup never resolves through a tombstoned
            // membership row.
            m.where("removed_at", "IS", null).related("user"),
          )
          .related("parent"),
      )
      // Composite-key `.related()` — uses the new
      // channel_members.stats relationship on (channel_id, user_id). First
      // composite-key relationship user in this repo.
      .related("stats"),
  ),

  /**
   * Spaces the user belongs to, with their child channels + workspace links.
   * Dedicated path so sidebar grouping logic doesn't share an IVM pipeline
   * with the per-channel membership stream.
   *
   * NOTE: defined but not yet consumed by Sidebar.tsx — the Sidebar today
   * uses the alias-namespaced `client_spaces.*` queries. Reserved for a
   * follow-up cleanup PR that wires this idiomatic shape through.
   */
  mySpacesTree: defineQuery(({ ctx: { sub } }) =>
    zql.channel_members
      .where("user_id", sub)
      .where("removed_at", "IS", null)
      .whereExists("channel", (cq) => cq.where("type", "space"))
      .related("channel", (cq) =>
        cq.related("child_channels").related("workspace_links"),
      ),
  ),
};
